import { UserIdentityModel } from "client/Api/UserProfile/service";
import { TimeZone } from "client/DateAndTime/timezones";
import { isNil } from "client/Misc/utils";
import {
	PropsWithChildren,
	ReactElement,
	useMemo
} from "react";
import { getComputedDisplayName } from "../AddressBook/common";
import {
	Anchor,
	DateString,
	FormattedText
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { isValidDate } from "client/DateAndTime/dateTimeUtils";
import styles from "./History.module.less";

export const History = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<p className={styles.root}>
			{children}
		</p>
	);
};
History.displayName = "History";

export type HistoryDateProps = {
	dateName: LocalizationKey;
	date: Date | string;
	user: UserIdentityModel | null;
	onClickAuthor?: (authorId: number, authorName: string) => void;
	trackingId?: string;
	timezone: TimeZone;
};

const HistoryDate = (props: HistoryDateProps): ReactElement => {
	const {
		dateName,
		date,
		user,
		onClickAuthor,
		trackingId,
		timezone
	} = props;

	const key: LocalizationKey = user
		? "_DateNameDateByUser"
		: "_DateNameDate";

	const author = useMemo(
		(): ReactElement | string => {
			if (isNil(user)) {
				return "";
			}

			const {
				computedDisplayName
			} = getComputedDisplayName({
				firstName: user.firstName,
				lastName: user.lastName
			});

			if (!onClickAuthor) {
				return computedDisplayName;
			}

			return (
				<Anchor
					trackingId={trackingId}
					className={styles.link}
					onClick={() => onClickAuthor(user.userId, computedDisplayName)}
				>
					{computedDisplayName}
				</Anchor>
			);
		},
		[
			onClickAuthor,
			trackingId,
			user
		]
	);

	return (
		<FormattedText
			text={localize(key)}
			thingsToReplace={[
				{ what: "{{DateName}}", filler: localize(dateName) },
				{
					what: "{{Date}}",
					filler: isValidDate(date)
						? (
							<DateString
								value={date}
								format="RelativeTimestamp"
								timezone={timezone}
							/>
						)
						: localize("_NoValidInput")
				},
				{ what: "{{User}}", filler: author }
			]}
		/>
	);
};
HistoryDate.displayName = "History.Date";

const HistorySeparator = (): ReactElement => {
	return (<>{" | "}</>);
};
HistorySeparator.displayName = "History.Separator";

History.Date = HistoryDate;
History.Separator = HistorySeparator;